import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/AuthContext";
import { refreshMaintenanceStatus } from "./maintenanceApi";
import { getMaintenance, isClosed, setMaintenance, useMaintenance } from "./maintenanceStore";

export const MAINTENANCE_PATH = "/maintenance";

/** How often the app asks while maintenance is off, and while it is on. */
const POLL_IDLE_MS = 60_000;
const POLL_ACTIVE_MS = 20_000;

/**
 * Wraps every route. While maintenance closes the app it sends the visitor,
 * signed in or not, to the maintenance page: the login page included. It never
 * touches the session, so a signed-in user is still signed in when maintenance
 * ends and is sent back to the page they were on.
 */
export function MaintenanceGate() {
  const status = useMaintenance();
  const location = useLocation();
  const { refreshSession } = useAuth();
  const closed = isClosed(status);
  const wasClosed = useRef(false);

  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    const controller = new AbortController();
    const check = async () => {
      window.clearTimeout(timer);
      if (document.visibilityState !== "hidden") {
        const next = await refreshMaintenanceStatus(controller.signal);
        if (stopped) return;
        if (next) setMaintenance(next);
      }
      if (!stopped) timer = window.setTimeout(check, getMaintenance().enabled ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };
    // A tab that sat in the background through a maintenance window checks the
    // moment it is shown again, not up to a poll interval later.
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    void check();
    return () => {
      stopped = true;
      controller.abort();
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // When maintenance ends, bring the session state back: requests that failed
  // while it was on left the auth state exactly as it was.
  useEffect(() => {
    if (wasClosed.current && !closed) void refreshSession();
    wasClosed.current = closed;
  }, [closed, refreshSession]);

  if (closed && location.pathname !== MAINTENANCE_PATH) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={MAINTENANCE_PATH} replace state={{ from }} />;
  }
  return <Outlet />;
}
